import React, { useState, useEffect } from "react";
import ToggleSwitch from "../common/ToggleSwitch";

export default function SoundSettingsModal({
  currentSettings,
  isVibrationMuted,
  isVoiceMuted,
  onSave,
  onClose,
  gameModes,
  onGameModeChange,
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [settings, setSettings] = useState(currentSettings);
  const [vibrationMuted, setVibrationMuted] = useState(isVibrationMuted);
  const [voiceMuted, setVoiceMuted] = useState(isVoiceMuted);

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handleToggle = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleVibrationToggle = (value) => {
    setVibrationMuted(!value);
  };

  const handleVoiceToggle = (value) => {
    setVoiceMuted(!value);
  };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-50 p-4 transition-colors duration-300 ${
        isAnimating ? "bg-black/70" : "bg-black/0"
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-[#f3e9d2] text-[#5a4a3a] p-6 rounded-lg shadow-2xl border-4 border-gray-700 w-full max-w-sm flex flex-col items-center gap-4 transition-all duration-300 ease-out ${
          isAnimating ? "scale-100 opacity-100" : "scale-75 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-3xl font-bold font-serif text-red-700 border-4 border-red-700 px-4 py-1 rounded-sm transform -rotate-3">
          ตั้งค่าเกม
        </p>

        {gameModes && (
          <div className="w-full text-left mt-2">
            <h4 className="font-bold font-serif text-lg text-[#5a4a3a] border-b border-gray-400/60 pb-1 mb-2">
              โหมดที่เปิดใช้งาน
            </h4>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="bg-[#e1d4be] text-[#6b4f3a] text-xs font-bold px-3 py-1 rounded-full border border-[#c8bda9] shadow-sm">
                เวลาพูดคุย: {gameModes.discussionTime || 60} วินาที
              </span>
              {gameModes.isRandomKillMode && (
                <span className="bg-[#e1d4be] text-[#6b4f3a] text-xs font-bold px-3 py-1 rounded-full border border-[#c8bda9] shadow-sm">
                  โหมดสุ่มฆ่า
                </span>
              )}
              {gameModes.isBeginnerMode && (
                <span className="bg-[#e1d4be] text-[#6b4f3a] text-xs font-bold px-3 py-1 rounded-full border border-[#c8bda9] shadow-sm">
                  โหมดมือใหม่
                </span>
              )}
              {!gameModes.isRandomKillMode &&
                !gameModes.isSpeedMode &&
                !gameModes.isBeginnerMode && (
                  <span className="bg-gray-100 text-gray-500 text-xs italic px-3 py-1 rounded-full border border-gray-300">
                    ไม่มีโหมดพิเศษ
                  </span>
                )}
            </div>
          </div>
        )}

        <hr className="w-full border-dashed border-gray-400/60 my-2" />

        <div className="w-full space-y-3">
          <ToggleSwitch
            icon="👆"
            label="เสียง UI"
            description="เสียงปุ่ม, พลิกการ์ด, เปิดสมุด (แนะนำ)"
            isEnabled={!settings.isUiMuted}
            onToggle={(val) => handleToggle("isUiMuted", !val)}
          />

          {/* --- เพิ่มส่วนตั้งค่าเสียงดนตรี (BGM) --- */}
          <ToggleSwitch
            icon="🎵"
            label="เสียงดนตรี"
            description="ดนตรีพื้นหลัง (เฉพาะหน้าแรกและล็อบบี้)"
            isEnabled={!settings.isBgmMuted}
            onToggle={(val) => handleToggle("isBgmMuted", !val)}
          />

          {/* --- ปรับปรุงคำอธิบายเสียงบรรยากาศ (Ambience) --- */}
          <ToggleSwitch
            icon="🔊"
            label="เสียงบรรยากาศ"
            description="เสียงเปลี่ยนเฟส, เอฟเฟกต์แจ้งเตือน (แนะนำ)"
            isEnabled={!settings.isAmbienceMuted}
            onToggle={(val) => handleToggle("isAmbienceMuted", !val)}
          />

          <ToggleSwitch
            icon="🗣️"
            label="เสียงผู้บรรยาย"
            description="เสียงพูดบรรยายสถานการณ์ต่างๆ"
            isEnabled={!voiceMuted}
            onToggle={handleVoiceToggle}
          />
          <ToggleSwitch
            icon="📳"
            label="การสั่น"
            description="เอฟเฟกต์การสั่นแจ้งเตือน (แนะนำ)"
            isEnabled={!vibrationMuted}
            onToggle={handleVibrationToggle}
          />
        </div>

        <div className="flex gap-4 w-full mt-4">
          <button
            onClick={handleClose}
            className="w-full bg-[#a08d70] hover:bg-[#8d7c62] text-white font-bold py-3 px-4 rounded-lg transition-transform active:scale-95 active:shadow-inner"
          >
            ยกเลิก
          </button>
          <button
            onClick={() => onSave(settings, vibrationMuted, voiceMuted)}
            className="w-full bg-[#556b2f] hover:bg-[#6B8E23] text-white font-bold py-3 px-4 rounded-lg transition-transform active:scale-95 active:shadow-inner"
          >
            บันทึก
          </button>
        </div>
      </div>
    </div>
  );
}
